import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { CANVAS, HAIRLINE } from "../../../theme/tokens";

// Shared content wrapper for MainPanel tabs.
// The parent owns outer page padding; this component handles width, an
// optional header row, and vertical stacking for the page body.
// By default it stays fluid. Pass `maxWidth` to cap and center the content.
export const CONTENT_MAX_WIDTH = 1440;
const MAX_WIDTH_PX = { sm: 600, md: 900, lg: 1200, xl: 1536 };

function resolveMaxWidth(maxWidth) {
  if (maxWidth == null) return "none";
  if (typeof maxWidth === "number") return maxWidth;
  return MAX_WIDTH_PX[maxWidth] ?? CONTENT_MAX_WIDTH;
}

export default function MainPanelContent({
  title,
  rightNode,
  subtitle,
  children,
  // Default to fluid width.
  maxWidth = null,
  titleVariant = "h4",
  headerMb = 2,
  contentGap = 1.5,
}) {
  const cap = resolveMaxWidth(maxWidth);
  const showHeader = title != null || rightNode != null || subtitle != null;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: cap,
        mx: "auto",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {showHeader && (
        <Box
          sx={{
            mb: headerMb,
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: CANVAS,
            pt: { xs: 3, sm: 4 },
            mt: { xs: -3, sm: -4 },
            borderBottom: `1px solid ${HAIRLINE}`,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              columnGap: 2,
              rowGap: 1,
              flexWrap: "wrap",
            }}
          >
            {title != null && (
              <Typography variant={titleVariant} sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
            )}

            {rightNode && (
              <Box
                sx={{
                  ml: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                {rightNode}
              </Box>
            )}
          </Box>
          {subtitle != null && (
            <Box sx={{ mt: 1 }}>
              {typeof subtitle === "string" ? (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              ) : (
                subtitle
              )}
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: contentGap,
          width: "100%",
          flex: 1,
          overflowX: "auto",
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

MainPanelContent.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  rightNode: PropTypes.node,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  titleVariant: PropTypes.string,
  headerMb: PropTypes.number,
  contentGap: PropTypes.number,
};
